<div class="space-y-5">
  @if (serverError) {
    <div class="rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ serverError }}
    </div>
  }

  <!-- ═══ Personne cible ═══════════════════════════════════════════════════ -->
  @if (mode === 'create') {
    <section class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.02]">
      <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">Personne à associer</h3>
      <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Recherchez par email, téléphone ou identifiant. Si la personne n'existe pas, vous pourrez créer un compte (elle recevra une invitation).
      </p>

      <!-- Étape recherche -->
      @if (personMode() === 'search') {
        <form [formGroup]="searchForm" (ngSubmit)="search()" class="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            formControlName="identifier"
            placeholder="email@exemple.sn · +221 77 … · username"
            class="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          />
          <button
            type="submit"
            [disabled]="resolving() || searchForm.invalid"
            class="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            @if (resolving()) {
              <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
              Recherche…
            } @else { Rechercher }
          </button>
        </form>
        @if (resolveError()) {
          <p class="mt-2 text-xs text-error-600 dark:text-error-400">{{ resolveError() }}</p>
        }
      }

      <!-- Personne trouvée -->
      @else if (personMode() === 'found') {
        <div class="mt-3 flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 dark:border-brand-500/30 dark:bg-brand-500/10">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-gray-800 dark:text-white/90">{{ personLabel() }}</p>
            <p class="truncate text-xs text-gray-500 dark:text-gray-400">
              {{ resolvedUser()?.email || resolvedUser()?.phone || resolvedUser()?.username }}
            </p>
          </div>
          <button type="button" (click)="resetPerson()" class="shrink-0 text-xs font-medium text-brand-700 hover:text-brand-800 dark:text-brand-400">
            Changer
          </button>
        </div>
      }

      <!-- Nouveau compte (notice ; les champs sont dans le formulaire ci-dessous) -->
      @else {
        <div class="mt-3 flex items-center justify-between gap-3">
          <span class="inline-flex items-center gap-1.5 rounded-full bg-warning-50 px-2.5 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-orange-400">
            Aucun compte trouvé — nouveau compte
          </span>
          <button type="button" (click)="resetPerson()" class="shrink-0 text-xs font-medium text-brand-700 hover:text-brand-800 dark:text-brand-400">
            Changer
          </button>
        </div>
      }
    </section>
  } @else {
    <!-- Édition : personne figée -->
    <section class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
      <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Personne liée</p>
      <p class="mt-0.5 text-sm font-medium text-gray-800 dark:text-white/90">
        {{ initial?.user ? fullName(initial!.user!) : ('Utilisateur #' + (initial?.userId ?? '—')) }}
      </p>
    </section>
  }

  <!-- ═══ Formulaire opérateur (+ champs nouveau compte) ═══════════════════ -->
  @if (mode === 'edit' || personMode() !== 'search') {
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-5">
      <!-- Champs nouveau compte (mode 'new' uniquement) -->
      @if (mode === 'create' && personMode() === 'new') {
        <div class="grid gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Prénom *</label>
            <input type="text" formControlName="firstName"
              class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nom *</label>
            <input type="text" formControlName="lastName"
              class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          </div>
        </div>
      }

      <div class="grid gap-5 sm:grid-cols-2">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nom commercial *</label>
          <input type="text" formControlName="businessName" placeholder="ex. Plateau Mobilités"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          @if (showError('businessName')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">Nom commercial requis (max 200 caractères).</p>
          }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zone d'opération *</label>
          <app-pretty-select
            formControlName="zoneCode"
            [options]="zoneOptions()"
            [placeholder]="zonesLoading() ? 'Chargement des zones…' : 'Choisir une zone'"
            searchable
          />
          @if (zonesError()) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">
              {{ zonesError() }}
              <button type="button" (click)="loadZones()" class="font-medium underline">Réessayer</button>
            </p>
          }
          @if (showError('zoneCode')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">Zone requise.</p>
          }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Palier de licence</label>
          <app-pretty-select formControlName="licenseTier" [options]="tierOptions" placeholder="Aucun palier" />
        </div>

        <div></div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Téléphone @if (mode === 'create' && personMode() === 'new') { <span class="text-error-500">*</span> }
          </label>
          <input type="tel" formControlName="phone" placeholder="+221 77 …"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email @if (mode === 'create' && personMode() === 'new') { <span class="text-error-500">*</span> }
          </label>
          <input type="email" formControlName="email" placeholder="contact@operateur.sn"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          @if (showError('email')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">Email invalide.</p>
          }
        </div>
      </div>

      @if (mode === 'create' && personMode() === 'new') {
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Email <strong>et/ou</strong> téléphone requis — la personne recevra une invitation pour définir son mot de passe.
        </p>
      }

      <div class="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-5 dark:border-gray-800">
        <button type="button" (click)="onCancel()" [disabled]="submitting"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
          Annuler
        </button>
        <button type="submit"
          [disabled]="submitting || form.invalid || (mode === 'create' && personMode() === 'new' && newAccountInvalid())"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (submitting) {
            @if (mode === 'create') { Création… } @else { Enregistrement… }
          } @else {
            @if (mode === 'create') { Créer (PENDING) } @else { Enregistrer }
          }
        </button>
      </div>
    </form>
  }
</div>
